import React, { lazy, Suspense } from "react";
import HeroV2 from "./HeroV2";
// Eager, not lazy — this now renders above HeroV2 (top of page, directly
// under the fixed header), so it's part of the critical above-the-fold
// paint. Code-splitting it would leave a blank Suspense gap there before
// popping in and shoving Hero down (a real CLS hit), unlike below-the-fold
// sections where a brief lazy-load delay is invisible.
import NationalDayOfferBanner from "./NationalDayOfferBanner";
import useIsMobile from "@/hooks/useIsMobile";
import afterFirstInteraction from "@/utils/afterFirstInteraction";

// Below-fold sections: on a pre-rendered (hydrated) page their code loads on
// the first interaction; the server HTML stays visible until then.
const deferredLazy = (factory) => lazy(() => afterFirstInteraction().then(factory));

// Below-the-fold sections are code-split via React.lazy(), each wrapped in
// its own <Suspense fallback={PENDING}> — not gated behind an
// IntersectionObserver (see Components/LazySection.jsx for why that broke
// SSR). This keeps the initial page-chunk small for the LCP-critical Hero
// while still rendering real content server-side once the SSR process has
// warmed a page shape at least once.
const Intro = deferredLazy(() => import("./Intro"));
const HomeTrustSections = deferredLazy(() => import("./HomeTrustSections"));
const WhyBestMarketing = deferredLazy(() => import("./WhyBestMarketing"));
const Industries = deferredLazy(() => import("./Industries"));
const VideoIntro = deferredLazy(() => import("./VideoIntro"));
const GrowthBanner = deferredLazy(() => import("./GrowthBanner"));
const TootiMoneyBanner = deferredLazy(() => import("./TootiMoneyBanner"));
// Tabbed showcase of the 6 flagship services, restored in place of the
// simpler category-card grid.
const ServicesShowcase = deferredLazy(() => import("./ServicesShowcase"));
const ServicesConfigurator = deferredLazy(() => import("./ServicesConfigurator"));
const LatestBlogs = deferredLazy(() => import("./LatestBlogs"));
// Same map + form section as the /contact page — replaced the older
// "دعنا نساعدك" trust-card form here per request (Home/Contact.jsx kept,
// not deleted, in case it's wanted back).
const ContactMapSection = deferredLazy(() => import("../Contact/ContactMapSection"));
const HowItWorksMobile = deferredLazy(() => import("./HowItWorksMobile"));
const FaqSection = deferredLazy(() => import("@/Components/FaqSection"));
const ClientLogosMarquee = deferredLazy(() => import("@/Components/ClientLogosMarquee"));
const GoogleReviewsMarquee = deferredLazy(() => import("@/Components/GoogleReviewsMarquee"));
const ContractBenefits = deferredLazy(() => import("./ContractBenefits"));
const CertifiedPartnersSection = deferredLazy(() =>
    import("../Services/ServicesPages/AdsManagementSections/CertifiedPartnersSection"),
);
const IntegratedPlatformBanner = deferredLazy(() => import("./IntegratedPlatformBanner"));
const WhyChooseGrid = deferredLazy(() => import("./WhyChooseGrid"));
const SocialShowcase = deferredLazy(() => import("./SocialShowcase"));
const SuccessStories = deferredLazy(() => import("./SuccessStories"));
const ComparisonTable = deferredLazy(() => import("./ComparisonTable"));
const ClientJourneySection = deferredLazy(() => import("./ClientJourneySection"));
const WorksReelMobile = deferredLazy(() => import("./WorksReelMobile"));
const ServiceAreaSection = deferredLazy(() => import("./ServiceAreaSection"));
const SixPlusSixOfferBanner = deferredLazy(() => import("./SixPlusSixOfferBanner"));
const MobileFinalCTA = deferredLazy(() => import("./MobileFinalCTA"));

// Placeholder for a below-the-fold section whose chunk is still loading.
// With a null fallback the page started out collapsed, so the footer and
// later sections sat within the browser's lazy-load distance and their
// loading="lazy" images all downloaded before the visitor scrolled. A
// viewport-tall block keeps the page near its real length meanwhile. Every
// boundary is below the hero, so the swap never shifts what is on screen.
const PENDING = <div style={{ minHeight: "100vh" }} aria-hidden="true" />;

export default function Index({ faq, latestBlogs, clientLogos, youtubeShorts, adsCaseStudies, showcaseReels }) {
    const isMobile = useIsMobile();

    return (
        <div className="wrapper home-page">
            {/* Seasonal — Saudi National Day 50%-off banner. Removed from
                mobile; still renders on PC (after the hero) below. */}

            {/* LCP Critical Path: Render Hero immediately */}
            <HeroV2 />

            {/* National Day offer banner hidden on all devices per request
                (2026-10-07) — left in place, flip to !isMobile to restore. */}
            {false && (
                <section className="pt-6 md:pt-10 lg:pt-12 bg-white">
                    <NationalDayOfferBanner />
                </section>
            )}

            {/* Video proof, right after the hero (and the banner on PC),
                then the trust-logo strip right after that — both standalone
                since ClientLogosMarquee already self-pads (shared across
                other pages too); giving VideoIntro matching top-only padding
                here instead of folding it into the space-y-* section below
                avoids doubling that padding. */}
            <section className="pt-6 md:pt-10 lg:pt-12 bg-white">
                <Suspense fallback={PENDING}>
                    <div className="px-4 md:px-0"><VideoIntro /></div>
                </Suspense>
            </section>

            {/* Simple icon/description/"learn more" grid of the site's
                core real services with real internal links, followed by
                the client fulfillment journey — right after the video
                proof, shows exactly how we deliver: client sends project
                → team works → we finish → we follow up. */}
            {/* Mobile top/bottom padding reduced (py-12→py-6) so this
                section's boundary with the next one isn't ~96px of combined
                padding on top of the space-y gaps — md/lg untouched. */}
            <section className="py-6 md:py-10 lg:py-12 bg-white">
                <Suspense fallback={PENDING}>
                    <div className="space-y-6 lg:space-y-8">
                        <div className="lg:px-6"><ServicesConfigurator /></div>

                        {/* Merged into one continuous card on mobile — image
                            caps the top, journey content flows directly
                            below with no seam/shadow break between them
                            (outer wrapper owns the rounding + shadow, so
                            TootiMoneyBanner/ClientJourneySection stay plain
                            internally). Desktop renders ClientJourneySection
                            alone, completely unchanged. */}
                        {isMobile && <div className="px-4"><WorksReelMobile /></div>}
                        {isMobile ? (
                            <div className="!mt-6 overflow-hidden rounded-[24px] bg-white shadow-[0_8px_24px_-8px_rgba(37,99,235,0.18)]">
                                <TootiMoneyBanner />
                                <div className="pt-6 pb-2">
                                    <ClientJourneySection />
                                </div>
                            </div>
                        ) : (
                            <ClientJourneySection />
                        )}
                    </div>
                </Suspense>
            </section>

            {/* Growth/results gradient banner — mobile gets the globe/orbit
                scene (GrowthBanner.jsx's own lg:hidden branch), desktop/
                tablet keeps the real supplied photo (hidden lg:block branch,
                same pattern as the blog/portfolio hero sections). No
                "hidden" gate at this wrapper level any more — GrowthBanner
                itself now decides per breakpoint. */}
            <section className="pt-6 md:pt-10 lg:pt-12 bg-white">
                <Suspense fallback={PENDING}>
                    <div className="hidden lg:block lg:px-6"><GrowthBanner /></div>
                </Suspense>
            </section>

            <Suspense fallback={PENDING}>
                <ClientLogosMarquee />
            </Suspense>

            {/* Services hub moved here per request (2026-10-12): right after
                the growth banner + client logos. */}
            <section className="pt-6 md:pt-10 lg:pt-12 bg-white">
                <Suspense fallback={PENDING}>
                    <ServicesShowcase />
                </Suspense>
            </section>

            {/* "Why WizFreelance" — the full trust story (why us →
                certification → feature comparison → head-to-head vs.
                competitors) as one continuous section with shared padding,
                instead of stacked sections each re-padding the same idea.
                Closes with a single CTA. */}
            {/* Mobile top/bottom padding reduced (py-12→py-6), same reason
                as the section above — md/lg untouched. */}
            <section className="py-6 md:py-10 lg:py-12 bg-white">
                <Suspense fallback={PENDING}>
                    <div className="space-y-6 lg:space-y-8">
                        {/* Seasonal — "6 months on you, 6 months on us"
                            social-media-management offer. Moved into this
                            section (instead of its own standalone section
                            above) so it flows with the next block on PC
                            rather than sitting isolated before it. Hidden on
                            mobile for now — the "hidden" utility class only
                            sets display:none, not the HTML hidden attribute,
                            so this wrapper still counts as a sibling for
                            space-y-* purposes and Intro below keeps the
                            standard rhythm either way; no margin override
                            needed on it. */}
                        {/* 6+6 offer hidden on PC too per request
                            (2026-10-07) — was "hidden lg:block". */}
                        <div className="hidden">
                            <SixPlusSixOfferBanner />
                        </div>

                        {/* Success stories right after "من نحن" (2026-10-17). */}
                        <Intro beforeCta={<SuccessStories />} />

                        <HomeTrustSections />

                        <div className="hidden md:block"><WhyBestMarketing /></div>

                        <IntegratedPlatformBanner />

                        {/* CertifiedPartnersSection hidden: duplicated by HomeTrustSections partners band (2026-10-12). */}

                        {/* WhyChooseGrid hidden per request (2026-10-12). */}

                        {/* Moved here per request — right after "لماذا
                            يختارنا أكثر من 1000 شركة؟" (WhyChooseGrid), was
                            previously near the page bottom after
                            ServiceAreaSection. Mobile-only per request —
                            removed from desktop/tablet (lg+). */}
                        {/* Hidden on mobile too per request (2026-10-07) —
                            was "lg:hidden"; left in place, not deleted. */}


                        <SocialShowcase reels={showcaseReels} />

                        {/* Hidden on mobile per request — still shown on desktop/tablet. */}
                        {/* ComparisonTable hidden per request (2026-10-12). */}
                    </div>
                </Suspense>
            </section>

            {/* Mobile-only — no equivalent exists on desktop, so it's
                skipped entirely there rather than hidden with CSS. */}
            {isMobile && (
                <div className="md:hidden">
                    <Suspense fallback={PENDING}>
                        <HowItWorksMobile />
                    </Suspense>
                </div>
            )}

            {/* Success stories moved inside Intro, right after "من نحن" (2026-10-17). */}

            {/* Was mobile-only (lg:hidden) and never shown on desktop/tablet
                either — now hidden on mobile too per request, so this
                section no longer renders on any breakpoint. Left in place
                (not deleted) in case it's wanted back later. */}


            {/* Industries teaser doubles as the sectors/growth pitch —
                SectorGrowth covered the same idea with an older 8-sector
                taxonomy, so it's retired here rather than showing both. */}
            <Suspense fallback={PENDING}>
                <Industries />
            </Suspense>

            <Suspense fallback={PENDING}>
                <ServiceAreaSection />
            </Suspense>


            {/* Reviews + FAQ + what's-included checklist + contact form.
                Each child already self-pads (GoogleReviewsMarquee, FaqSection,
                ContractBenefits and Contact are all shared across other pages
                too, so their own padding stays put) — this wrapper only sets
                the shared background; adding padding or a gap here would
                double up every gap in this sequence. */}
            {/* Hidden per request — was a mobile-only closing CTA right
                before the form. Left in place (not deleted) in case it's
                wanted back later. */}
            <div className="hidden">
                <Suspense fallback={PENDING}>
                    <MobileFinalCTA />
                </Suspense>
            </div>

            <section className="bg-white">
                <Suspense fallback={PENDING}>
                    {/* GoogleReviewsMarquee hidden per request (2026-10-12) */}


                    {/* Hidden on mobile per request — still shown on desktop/tablet. */}
                    <div className="hidden md:block">
                        <ContractBenefits />
                    </div>

                    <div className="hidden md:block">
                        <ContactMapSection />
                    </div>
                </Suspense>
            </section>

            {/* Last section on the page, directly before the footer. Hidden
                on mobile per request — still shown on desktop/tablet. */}
            {latestBlogs && latestBlogs.length > 0 && (
                <div className="hidden md:block">
                    <Suspense fallback={PENDING}>
                        <LatestBlogs blogs={latestBlogs} />
                    </Suspense>
                </div>
            )}

            {/* FAQ is the last section, right before the footer (2026-10-12). */}
            {faq && (
                <Suspense fallback={PENDING}>
                    <FaqSection data={faq} />
                </Suspense>
            )}
        </div>
    );
}
